import React from "react";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";

import {
  StyledDialogContent,
  IconWrapper,
} from "../../pages/selller/dashboards/styles";
import {
  dialogPaperSx,
  closeBoxSx,
  outerPaddingSx,
  tableContainerSx,
  tableSx,
  headerCellSx,
  clampTwoLines,
  getBodyCellSx,
} from "./outletDialog.styles";

export interface OutletRow {
  outletName: string;
  email: string;
  outstandingAmount?: number | string;
}

interface CustomDialogProps {
  open: boolean;
  onClose: () => void;
  rows: OutletRow[];
  maxWidth?: "sm" | "md" | "lg" | "xl" | false;
  emptyMessage?: string;
  showOutstanding?: boolean;
  formatOutstanding?: (v: number | string | undefined) => string;
}

export default function OutletDialog({
  open,
  onClose,
  rows,
  maxWidth = "md",
  emptyMessage,
  showOutstanding = false,
  formatOutstanding,
}: CustomDialogProps) {
  const hasRows = Array.isArray(rows) && rows.length > 0;
  const colCount = showOutstanding ? 3 : 2;
  const colWidth = `${100 / colCount}%`;

  const fmtOutstanding =
    formatOutstanding ??
    ((v) => {
      const n =
        typeof v === "string"
          ? Number(v.toString().replace(/,/g, ""))
          : Number(v ?? 0);
      if (Number.isNaN(n)) return "-";
      return n.toLocaleString("en-IN");
    });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      aria-labelledby="custom-dialog-title"
      PaperProps={{ sx: dialogPaperSx }}
    >
      <Box sx={closeBoxSx}>
        <IconWrapper onClick={onClose} role="button" aria-label="Close dialog">
          <IconButton size="small" aria-label="close">
            <CloseIcon />
          </IconButton>
        </IconWrapper>
      </Box>

      <StyledDialogContent sx={{ padding: 0 }}>
        <Box sx={outerPaddingSx}>
          <TableContainer component={Paper} sx={tableContainerSx}>
            <Table aria-label="outlets table" sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ ...headerCellSx, width: colWidth }}
                    align="center"
                  >
                    Outlet Name
                  </TableCell>

                  <TableCell
                    sx={{ ...headerCellSx, width: colWidth }}
                    align="center"
                  >
                    Email Id
                  </TableCell>

                  {showOutstanding && (
                    <TableCell
                      sx={{ ...headerCellSx, width: colWidth }}
                      align="center"
                    >
                      Outstanding
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {hasRows ? (
                  rows.map((r, idx) => {
                    const isLast = idx === rows.length - 1;
                    const bodyCellSx = (w: string) => ({
                      ...getBodyCellSx(isLast),
                      width: w,
                    });

                    return (
                      <TableRow key={`${r.outletName}-${idx}`}>
                        <TableCell
                          component="th"
                          scope="row"
                          align="center"
                          sx={bodyCellSx(colWidth)}
                          title={r.outletName}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, ...clampTwoLines }}
                          >
                            {r.outletName}
                          </Typography>
                        </TableCell>

                        <TableCell
                          align="center"
                          sx={bodyCellSx(colWidth)}
                          title={r.email}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: r.email
                                ? "primary.main"
                                : "text.secondary",
                              ...clampTwoLines,
                              cursor: r.email ? "pointer" : "default",
                              textDecoration: r.email ? "underline" : "none",
                              "&:hover": r.email
                                ? {
                                    color: "primary.dark",
                                  }
                                : {},
                            }}
                            onClick={() => {
                              if (r.email) {
                                window.location.href = `mailto:${r.email}`;
                              }
                            }}
                          >
                            {r.email || "-"}
                          </Typography>
                        </TableCell>

                        {showOutstanding && (
                          <TableCell align="center" sx={bodyCellSx(colWidth)}>
                            <Typography
                              variant="body2"
                              sx={{ color: "text.secondary", ...clampTwoLines }}
                            >
                              {fmtOutstanding(r.outstandingAmount)}
                            </Typography>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={colCount}
                      align="center"
                      sx={{ py: 6, ...getBodyCellSx(true) }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        {emptyMessage || "No accounts"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </StyledDialogContent>
    </Dialog>
  );
}
